import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CenteredHeaderComponent } from '@chefami-monorepo/ui/centered-header';
import { FooterComponent } from '@chefami-monorepo/ui/footer';
import { AuthService, StorageService } from '@chefami-monorepo/data-access';
import { SiteCookies, UserData, SiteStorage } from '@chefami-monorepo/types';
import { SubscriptionStatus } from './constants/global';
import { HeaderComponent } from './components/header/header.component';
import { AgeModalComponent } from './modals/age-modal/age-modal.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    RouterLink,
    FooterComponent,
    CenteredHeaderComponent,
    HeaderComponent,
    AgeModalComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly ageModalId = `age-modal`;
  // Age modal (and its @angular/forms dependency) is lazy-loaded via @defer only when needed
  readonly showAgeModal = signal(false);

  readonly userData = this.auth.userData;
  readonly siteInfo = this.auth.siteInfo;
  readonly isLoggedIn = this.auth.isLoggedIn;

  readonly headerFooterOptions = computed<UserData>(() => ({
    isLoggedIn: this.isLoggedIn() ?? false,
    userInfo: this.userData()?.userInfo ?? undefined,
    siteInfo: this.siteInfo(),
  }));

  // Site-wide notice from site data, shown to logged-in users.
  readonly pageNotification = computed(() =>
    this.auth.userInfo() ? (this.siteInfo()?.notification ?? '') : '',
  );

  readonly isAccountDeactivated = computed(() => {
    const status = this.auth.userInfo()?.subscription_status;
    if (status === undefined) return false;
    return (
      status !== SubscriptionStatus.ACTIVE &&
      status !== SubscriptionStatus.PAUSED
    );
  });

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes('/profile')) {
          this.displayAgeModal();
        }
      });
  }

  displayAgeModal(): void {
    const isOrderPlaced =
      this.storage.getCookie(SiteCookies.ORDER_PLACED) === 'true';
    const hasAge = !!this.auth.userInfo()?.age;
    const isAgeModalViewed = this.storage.getItem<string>(
      SiteStorage.IS_AGE_MODAL_VIEWED,
      true,
    );

    if (!isOrderPlaced && !hasAge && !isAgeModalViewed) {
      this.showAgeModal.set(true);
      this.storage.setItem(SiteStorage.IS_AGE_MODAL_VIEWED, true, true);
    }
  }
}
